import { ProtectedRoute } from "@/components/shared/ProtectedRoute";
import { RbacRoute } from "@/components/shared/RbacRoute";
import { PageLoader, RoutePage } from "@/components/shared/RoutePage";
import { SupplierRouteGuard } from "@/components/shared/SupplierRouteGuard";
import { useAiAssistantEnabled } from "@/features/settings/hooks/useSettings";
import { AuthLayout } from "@/layouts/AuthLayout";
import { AuthSplitLayout } from "@/layouts/AuthSplitLayout";
import { DashboardLayout } from "@/layouts/DashboardLayout";
import { checkSubscriptionPermission } from "@/lib/permissions";
import { useAuthStore } from "@/stores/auth";
import { usePermissionsStore } from "@/stores/permissions";
import { lazy, type ComponentType, type ReactElement } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

function RegisterRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set("mode", "signup");
  return <Navigate to={`/login?${params.toString()}`} replace />;
}

function SettingsEntryRedirect() {
  const role = useAuthStore((state) => state.user?.role);
  const loaded = usePermissionsStore((state) => state.subscriptionLoaded);
  const grants = usePermissionsStore((state) => state.subscriptionPermissions);
  const addons = usePermissionsStore((state) => state.addonPermissions);
  if (role === 1) {
    if (!loaded) return <PageLoader />;
    if (!checkSubscriptionPermission("DASHBOARD", grants, -1, addons))
      return <Navigate to="/settings/security" replace />;
  }
  return <Navigate to="/settings/organisationInformation" replace />;
}

function lazyRoute(factory: () => Promise<{ default: ComponentType }>) {
  const Component = lazy(factory);
  return (
    <RoutePage>
      <Component />
    </RoutePage>
  );
}

/** Wrap an element with RBAC + subscription plan route gating. */
function gated(
  permission: string | string[] | undefined,
  planPermission: string | undefined,
  element: ReactElement,
) {
  return (
    <RbacRoute permission={permission} planPermission={planPermission}>
      {element}
    </RbacRoute>
  );
}

/** Redirect away from AI features when the organisation has disabled them. */
function AiRouteGuard({ children }: { children: ReactElement }) {
  const aiEnabled = useAiAssistantEnabled();
  return aiEnabled ? children : <Navigate to="/home" replace />;
}

// ── Pages ──
const Login = lazyRoute(() =>
  import("@/pages/Login").then((m) => ({ default: m.Login })),
);
const ErrorPage = lazyRoute(() => import("@/pages/ErrorPage"));
const ForgotPasswordPage = lazyRoute(
  () => import("@/pages/ForgotPasswordPage"),
);
const ResetPasswordPage = lazyRoute(() => import("@/pages/ResetPasswordPage"));
const VerifyEmailPage = lazyRoute(() => import("@/pages/VerifyEmailPage"));
const VerifyRegisterPage = lazyRoute(
  () => import("@/pages/VerifyRegisterPage"),
);
const OnBoardingPage = lazyRoute(() => import("@/pages/OnBoardingPage"));
const ExternalVerifierLoginPage = lazyRoute(
  () => import("@/pages/ExternalVerifierLoginPage"),
);
const SupplierRegistrationPage = lazyRoute(
  () => import("@/pages/SupplierRegistrationPage"),
);
const OwnershipHandoverPage = lazyRoute(
  () => import("@/pages/OwnershipHandoverPage"),
);
const SubscriptionPlansPage = lazyRoute(
  () => import("@/pages/SubscriptionPlansPage"),
);
const DataCollectionPage = lazyRoute(
  () => import("@/pages/DataCollectionPage"),
);
const VerifyAccountPage = lazyRoute(() => import("@/pages/VerifyAccountPage"));
const ResetSuccessPage = lazyRoute(() => import("@/pages/ResetSuccessPage"));

// ── Features ──
const ChatPage = lazyRoute(() =>
  import("@/features/ai/chat/ChatPage").then((m) => ({ default: m.ChatPage })),
);
const HomePage = lazyRoute(() => import("@/features/home/HomePage"));
const DashboardPage = lazyRoute(
  () => import("@/features/dashboard/DashboardPage"),
);
const EmissionsPage = lazyRoute(
  () => import("@/features/emissions/EmissionsPage"),
);
const TargetsPage = lazyRoute(() => import("@/features/targets/TargetsPage"));
const AssetsPage = lazyRoute(() => import("@/features/assets/AssetsPage"));
const OffsetsPage = lazyRoute(() =>
  import("@/features/offsets/OffsetsPage").then((m) => ({
    default: m.OffsetsPage,
  })),
);
const FacilitiesPage = lazyRoute(() =>
  import("@/features/facilities/FacilitiesPage").then((m) => ({
    default: m.FacilitiesPage,
  })),
);
const EmployeesPage = lazyRoute(() =>
  import("@/features/employees/EmployeesPage").then((m) => ({
    default: m.EmployeesPage,
  })),
);
const DisclosuresPage = lazyRoute(
  () => import("@/features/disclosures/pages/DisclosuresPage"),
);
const FactorsPage = lazyRoute(() => import("@/features/factors/FactorsPage"));
const AuditsPage = lazyRoute(() => import("@/features/audits/AuditsPage"));
const CustomersPage = lazyRoute(
  () => import("@/features/customers/CustomersPage"),
);
const SuppliersPage = lazyRoute(
  () => import("@/features/suppliers/SuppliersPage"),
);
const CategoryMappingPage = lazyRoute(
  () => import("@/features/category-mapping/pages/CategoryMappingPage"),
);
const ResourceCentrePage = lazyRoute(
  () => import("@/features/resource-centre/ResourceCentrePage"),
);
const CreateReportPage = lazyRoute(
  () => import("@/features/reports/pages/CreateReportPage"),
);
const ReportAssetPage = lazyRoute(
  () => import("@/features/reports/pages/ReportAssetPage"),
);
const AgentActivityPage = lazyRoute(
  () => import("@/features/agents/AgentActivityPage"),
);
const BulkUploadPage = lazyRoute(
  () => import("@/features/analytics/pages/BulkUploadPage"),
);
const UploadActivityPage = lazyRoute(
  () => import("@/features/emissions/pages/UploadActivityPage"),
);
const BriefingPage = lazyRoute(() =>
  import("@/features/analytics/pages/BriefingPage").then((m) => ({
    default: m.BriefingPage,
  })),
);

// ── Settings ──
const SettingsLayout = lazyRoute(
  () => import("@/features/settings/SettingsLayout"),
);
const OrganisationInfoPage = lazyRoute(
  () => import("@/features/settings/pages/OrganisationInfoPage"),
);
const OrganisationPreferencePage = lazyRoute(
  () => import("@/features/settings/pages/OrganisationPreferencePage"),
);
const SecurityPage = lazyRoute(
  () => import("@/features/settings/pages/SecurityPage"),
);
const TransferAccountPage = lazyRoute(
  () => import("@/features/settings/pages/TransferAccountPage"),
);
const ExchangeRatesPage = lazyRoute(
  () => import("@/features/settings/pages/ExchangeRatesPage"),
);
const SubscriptionSettingsPage = lazyRoute(
  () => import("@/features/settings/pages/SubscriptionSettingsPage"),
);

// ── Settings sub-pages ──
const AuditSummaryPage = lazyRoute(
  () => import("@/features/audits/pages/AuditSummaryPage"),
);

export function Router() {
  return (
    <Routes>
      <Route element={<AuthLayout />}>
        <Route
          path="/verifypassword"
          element={<Navigate to="/forgotpassword" replace />}
        />
        <Route path="/auth/register" element={<RegisterRedirect />} />
        <Route path="/auth/verify-email/:key" element={VerifyEmailPage} />
        <Route path="/auth/audit-request" element={ExternalVerifierLoginPage} />
        <Route
          path="/auth/ownership-handover"
          element={OwnershipHandoverPage}
        />
      </Route>

      <Route element={<AuthSplitLayout />}>
        <Route
          path="/auth/register-supplier"
          element={SupplierRegistrationPage}
        />
        <Route path="/login" element={Login} />
        <Route path="/forgotpassword" element={ForgotPasswordPage} />
        <Route path="/verifyaccount" element={VerifyAccountPage} />
        <Route path="/verifyotp" element={VerifyRegisterPage} />
        <Route path="/resetsuccess" element={ResetSuccessPage} />
        <Route path="/resetpassword" element={ResetPasswordPage} />
        <Route path="/corporate-onboarding" element={OnBoardingPage} />
      </Route>
      <Route path="/plans" element={SubscriptionPlansPage} />
      <Route path="/error" element={ErrorPage} />
      <Route path="/auth" element={<Navigate to="/login" replace />} />

      <Route
        element={
          <ProtectedRoute>
            <SupplierRouteGuard>
              <DashboardLayout />
            </SupplierRouteGuard>
          </ProtectedRoute>
        }
      >
        <Route path="/home" element={HomePage} />
        <Route path="/dashboard" element={DashboardPage} />
        <Route path="/chat" element={<AiRouteGuard>{ChatPage}</AiRouteGuard>} />
        <Route
          path="/log/*"
          element={gated("READ_EMISSIONS", "EMISSIONS", EmissionsPage)}
        />
        <Route
          path="/targets/*"
          element={gated("READ_TARGET_LIST", "TARGETS", TargetsPage)}
        />
        <Route
          path="/assets/*"
          element={gated("READ_ASSET_LIST", "ASSETS", AssetsPage)}
        />
        <Route
          path="/offsets/*"
          element={gated("READ_OFFSET_LIST", "OFFSETS", OffsetsPage)}
        />
        <Route
          path="/organisation/facilities/*"
          element={gated("READ_ALL_FACILITIES", "FACILITY", FacilitiesPage)}
        />
        <Route
          path="/organisation/employees/*"
          element={gated("READ_EMPLOYEE_LIST", "USER", EmployeesPage)}
        />
        <Route
          path="/organisation/disclosures"
          element={gated("READ_DISCLOSURE", "DISCLOSURE", DisclosuresPage)}
        />
        <Route
          path="/datasource/*"
          element={gated("READ_EMISSION_FACTOR", "EMISSIONS", FactorsPage)}
        />
        <Route
          path="/audits/*"
          element={gated("READ_AUDIT_REQUESTS_LIST", "AUDIT", AuditsPage)}
        />
        <Route path="/auditsummary" element={AuditSummaryPage} />
        <Route
          path="/partners/customers"
          element={gated("READ_CUSTOMER_LIST", undefined, CustomersPage)}
        />
        <Route
          path="/partners/customers/submit-data"
          element={DataCollectionPage}
        />
        <Route
          path="/partners/suppliers/*"
          element={gated("READ_SUPPLIER_LIST", "SRM", SuppliersPage)}
        />
        <Route path="/cat-map" element={CategoryMappingPage} />
        <Route path="/resourcecentre" element={ResourceCentrePage} />
        <Route
          path="/reports"
          element={gated("READ_REPORT", "REPORTS", CreateReportPage)}
        />
        <Route
          path="/report-asset"
          element={gated("READ_REPORT_ASSETS", undefined, ReportAssetPage)}
        />
        <Route
          path="/agents"
          element={<AiRouteGuard>{AgentActivityPage}</AiRouteGuard>}
        />
        <Route
          path="/bulk-upload"
          element={gated(
            "CREATE_EMISSION",
            "EMISSIONS",
            <AiRouteGuard>{BulkUploadPage}</AiRouteGuard>,
          )}
        />
        {/* Deliberately outside AiRouteGuard: AI intake is gated only at job
            creation, so in-flight batches must stay reviewable after an admin
            switches the assistant off. */}
        <Route
          path="/emissions/upload-activity"
          element={gated(undefined, "EMISSIONS", UploadActivityPage)}
        />
        <Route
          path="/analytics/briefing"
          element={<AiRouteGuard>{BriefingPage}</AiRouteGuard>}
        />

        <Route path="/settings" element={SettingsLayout}>
          <Route index element={<SettingsEntryRedirect />} />
          <Route
            path="organisationInformation"
            element={gated(
              "UPDATE_SETTINGS_INFO",
              "DASHBOARD",
              OrganisationInfoPage,
            )}
          />
          <Route
            path="organisationpreference"
            element={gated(
              "UPDATE_SETTINGS_INFO",
              "DASHBOARD",
              OrganisationPreferencePage,
            )}
          />
          <Route
            path="security"
            element={gated("UPDATE_SETTINGS_INFO", undefined, SecurityPage)}
          />
          <Route
            path="transfer-account"
            element={gated(
              "TRANSFER_OWNERSHIP",
              undefined,
              TransferAccountPage,
            )}
          />
          <Route
            path="exchangerates"
            element={gated(
              "UPDATE_SETTINGS_INFO",
              "DASHBOARD",
              ExchangeRatesPage,
            )}
          />
          <Route
            path="subscription"
            element={
              <RbacRoute permission="UPDATE_SUBSCRIPTION_PLAN">
                {SubscriptionSettingsPage}
              </RbacRoute>
            }
          />
        </Route>

        <Route path="*" element={<Navigate to="/home" replace />} />
      </Route>
    </Routes>
  );
}
